import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheck, Banknote, MapPin, ArrowRight, Phone, MessageCircle, HandCoins, BadgeCheck, Clock } from "lucide-react";
import { api, whatsappLink, PHONE, CATALOGUE_URL, formatApiErrorDetail } from "@/lib/api";
import { CarCard } from "@/components/CarCard";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const HERO = "https://images.unsplash.com/photo-1615966996783-5d361a011237?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjYXIlMjBmcm9udCUyMHZpZXd8ZW58MHx8fHwxNzg1MzMyMDY5fDA&ixlib=rb-4.1.0&q=85";
const SHOWROOM = "https://images.pexels.com/photos/20131971/pexels-photo-20131971.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const LOAN_IMG = "https://images.unsplash.com/photo-1773848089961-48796adfd588?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxjYXIlMjBoYW5kc2hha2V8ZW58MHx8fHwxNzgyODAxMjMwfDA&ixlib=rb-4.1.0&q=85";

const Home = () => {
  const { t } = useLang();
  const [cars, setCars] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", car_interested: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Shree Peteanjaneya Swamy Enterprises | Used Cars & Loans in Bengaluru";
    api.get("/cars").then((res) => setCars(res.data.slice(0, 6))).catch(() => {});
  }, []);

  const submitLoan = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error(t("loan.required"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/loan-enquiry", form);
      const text = `Loan Enquiry%0AName: ${form.name}%0APhone: ${form.phone}%0ACar Interested: ${form.car_interested || "-"}%0AMessage: ${form.message || "-"}`;
      toast.success(t("loan.success"));
      window.open(whatsappLink(decodeURIComponent(text)), "_blank");
      setForm({ name: "", phone: "", car_interested: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center">
        <div className="absolute inset-0">
          <img src={HERO} alt="Premium used car Bengaluru" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        </div>
        <div className="noise-overlay absolute inset-0" />

        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full pt-20">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#FF3B30] font-bold border border-[#FF3B30]/30 rounded-full px-4 py-1.5">
              <MapPin size={13} /> {t("hero.badge")}
            </span>
            <h1 className="font-display font-black text-4xl sm:text-6xl lg:text-7xl tracking-tight text-white mt-6 leading-[1.05]">
              {t("hero.title1")} <span className="text-[#FF3B30]">{t("hero.title2")}</span>
            </h1>
            <p className="text-gray-300 text-base sm:text-lg mt-6 max-w-xl">{t("hero.desc")}</p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/inventory">
                <Button className="bg-[#FF3B30] hover:bg-[#FF5247] text-white font-bold px-7 h-12 rounded-md text-base" data-testid="hero-browse-btn">
                  {t("hero.browse")} <ArrowRight size={18} className="ml-1" />
                </Button>
              </Link>
              <a href={whatsappLink("Hi, I'd like to know more about your cars.")} target="_blank" rel="noreferrer">
                <Button variant="outline" className="border-white/20 bg-white/5 hover:bg-white/10 text-white font-bold px-7 h-12 rounded-md text-base" data-testid="hero-whatsapp-btn">
                  <MessageCircle size={18} className="mr-1" /> {t("hero.chat")}
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-white/10 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/10">
          {[
            { icon: ShieldCheck, title: t("trust.transparency"), desc: t("trust.transparencyDesc") },
            { icon: Banknote, title: t("trust.loans"), desc: t("trust.loansDesc") },
            { icon: BadgeCheck, title: t("trust.trusted"), desc: t("trust.trustedDesc") },
          ].map((f) => (
            <div key={f.title} className="py-10 md:px-10 flex items-start gap-4">
              <div className="bg-[#FF3B30]/10 text-[#FF3B30] rounded-lg p-3"><f.icon size={24} /></div>
              <div>
                <h3 className="font-display font-bold text-lg text-white">{f.title}</h3>
                <p className="text-gray-400 text-sm mt-1">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED INVENTORY */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-gray-500 font-bold">{t("featured.label")}</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-white mt-2">{t("featured.title")}</h2>
          </div>
          <Link to="/inventory" className="text-[#FF3B30] font-semibold text-sm flex items-center gap-1 hover:gap-2 transition-all" data-testid="view-all-cars">
            {t("featured.viewAll")} <ArrowRight size={16} />
          </Link>
        </div>

        {cars.length === 0 ? (
          <div className="border border-dashed border-white/15 rounded-xl py-20 text-center">
            <p className="text-gray-400">{t("featured.empty")}</p>
            <a href={CATALOGUE_URL} target="_blank" rel="noreferrer" className="inline-block mt-4 text-[#25D366] font-semibold">{t("featured.catalogue")}</a>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cars.map((c, i) => <CarCard key={c.id} car={c} index={i} />)}
          </div>
        )}
      </section>

      {/* ABOUT */}
      <section id="about" className="bg-[#0d0d0d] border-y border-white/10 py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <p className="text-xs tracking-[0.2em] uppercase text-[#FF3B30] font-bold">{t("about.label")}</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-white mt-3">{t("about.title")}</h2>
            <p className="text-gray-400 mt-5 max-w-xl">{t("about.desc")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
              {[
                { icon: HandCoins, t: t("about.fair") },
                { icon: Clock, t: t("about.quick") },
                { icon: MapPin, t: t("about.karnataka") },
              ].map((x) => (
                <div key={x.t} className="bg-[#141414] border border-white/10 rounded-lg p-5 flex items-center gap-3">
                  <x.icon size={20} className="text-[#FF3B30]" />
                  <span className="text-white font-semibold text-sm">{x.t}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-5">
            <img src={SHOWROOM} alt="Car showroom Bengaluru" className="rounded-xl border border-white/10 w-full object-cover aspect-[4/3]" />
          </div>
        </div>
      </section>

      {/* LOAN */}
      <section id="loan" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <img src={LOAN_IMG} alt="Car loan approval" className="rounded-xl border border-white/10 w-full object-cover aspect-[4/3]" />
          <div className="mt-8">
            <p className="text-xs tracking-[0.2em] uppercase text-[#FF3B30] font-bold">{t("loan.label")}</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-white mt-3">{t("loan.title")}</h2>
            <p className="text-gray-400 mt-4 max-w-md">{t("loan.desc")}</p>
            <div className="flex gap-4 mt-6">
              <a href={`tel:${PHONE}`}><Button variant="outline" className="border-white/20 bg-white/5 hover:bg-white/10 text-white h-11 rounded-md" data-testid="loan-call-btn"><Phone size={16} className="mr-2" /> {t("loan.call")} {PHONE}</Button></a>
            </div>
          </div>
        </div>

        <div className="bg-[#141414] border border-white/10 rounded-2xl p-8">
          <h3 className="font-display font-bold text-xl text-white mb-6">{t("loan.formTitle")}</h3>
          <form onSubmit={submitLoan} className="space-y-5" data-testid="loan-form">
            <div>
              <Label className="text-gray-300 text-sm">{t("loan.name")} *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("loan.namePh")} className="mt-2 bg-black/40 border-white/20 focus:border-[#FF3B30] text-white" data-testid="loan-name" />
            </div>
            <div>
              <Label className="text-gray-300 text-sm">{t("loan.phone")} *</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("loan.phonePh")} className="mt-2 bg-black/40 border-white/20 focus:border-[#FF3B30] text-white" data-testid="loan-phone" />
            </div>
            <div>
              <Label className="text-gray-300 text-sm">{t("loan.car")}</Label>
              <Input value={form.car_interested} onChange={(e) => setForm({ ...form, car_interested: e.target.value })} placeholder={t("loan.carPh")} className="mt-2 bg-black/40 border-white/20 focus:border-[#FF3B30] text-white" data-testid="loan-car" />
            </div>
            <div>
              <Label className="text-gray-300 text-sm">{t("loan.message")}</Label>
              <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t("loan.messagePh")} className="mt-2 bg-black/40 border-white/20 focus:border-[#FF3B30] text-white" data-testid="loan-message" />
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-[#FF3B30] hover:bg-[#FF5247] text-white font-bold h-12 rounded-md" data-testid="loan-submit">
              {submitting ? t("loan.sending") : t("loan.submit")}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Home;
